import { useEffect, useState } from 'react';
import { getCategories, Category } from '../services/public';
import { getNavigationItems, NavigationItem } from '../services/navigation';
import { getEvents } from '../services/eventService';
import { getPlayers } from '../services/public';
import { getArticles } from '../services/articles';
import { getCachedYouTube } from '../services/youtube';
import { getZoneramaManifestWithFallbacks } from '../services/zonerama';
import { API_URL } from '../services/api';

export interface NavbarData {
  categories: Category[] | null;
  dynamicNavItems: NavigationItem[];
  navLoading: boolean;
  hasTables: boolean;
  hasActivities: boolean;
  hasPlayers: boolean;
  hasArticles: boolean;
  hasVideos: boolean;
  hasGallery: boolean;
}

export const useNavbarData = (isAdmin: boolean, settings?: any): NavbarData => {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [dynamicNavItems, setDynamicNavItems] = useState<NavigationItem[]>([]);
  const [navLoading, setNavLoading] = useState(true);
  const [hasTables, setHasTables] = useState<boolean>(false);
  const [hasActivities, setHasActivities] = useState<boolean>(false);
  const [hasPlayers, setHasPlayers] = useState<boolean>(false);
  const [hasArticles, setHasArticles] = useState<boolean>(false);
  const [hasVideos, setHasVideos] = useState<boolean>(false);
  const [hasGallery, setHasGallery] = useState<boolean>(false);

  // Combined data loading effect
  useEffect(() => {
    let active = true;
    
    const loadAllData = async () => {
      try {
        // Load navigation and categories in parallel
        const [navItems, cats] = await Promise.all([
          getNavigationItems().catch(() => []),
          getCategories().catch(() => [])
        ]);

        if (active) {
          // Process navigation
          const publicItems = Array.isArray(navItems) 
            ? navItems.filter(item => !item.requires_admin)
            : [];

          // Auto-seed if navigation is empty (only if user is authenticated as admin)
          if (publicItems.length === 0 && isAdmin) {
            try {
              console.log('Navigation empty, auto-seeding...');
              // Note: seedDefaultNavigation() would need to be imported
              // For now, continue with empty navigation
            } catch (seedError) {
              console.error('Auto-seed failed:', seedError);
            }
          }
          
          setDynamicNavItems(publicItems);

          // Process categories
          if (Array.isArray(cats) && cats.length > 0) {
            setCategories(cats);
          } else if (Array.isArray(settings?.categories)) {
            setCategories(settings.categories as any);
          } else {
            setCategories(null);
          }
        }
      } catch (error) {
        console.error('Failed to load navigation/categories:', error);
        if (active) {
          setDynamicNavItems([]);
          setCategories(Array.isArray(settings?.categories) ? settings.categories as any : null);
        }
      } finally {
        if (active) setNavLoading(false);
      }
    };

    loadAllData();
    return () => { active = false };
  }, [isAdmin, settings?.categories]);

  // Load content availability data in parallel
  useEffect(() => {
    let disposed = false;
    
    const resolveBackendUrl = (path: string) => {
      try {
        if (/^https?:\/\//i.test(path)) return path;
        if (path.startsWith('/cache') || path.startsWith('/uploads') || path.startsWith('/api/')) {
          const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
          return new URL(path, origin).toString();
        }
        return path;
      } catch { return path; }
    };

    const loadContentData = async () => {
      try {
        // Load all content availability checks in parallel
        const [
          tablesResponse,
          events,
          players,
          articlesResponse,
          youtube,
          manifest
        ] = await Promise.allSettled([
          fetch(resolveBackendUrl('/cache/prefetch/facr_tables.json'), { cache: 'no-cache' }),
          getEvents().catch(() => []),
          getPlayers().catch(() => []),
          getArticles({ page: 1, page_size: 1, published: true }).catch(() => ({ total: 0 })),
          getCachedYouTube().catch(() => null),
          getZoneramaManifestWithFallbacks().catch(() => [])
        ]);

        if (!disposed) {
          // Process tables
          if (tablesResponse.status === 'fulfilled') {
            const res = tablesResponse.value;
            if (res.ok) {
              const json = await res.json();
              const anyRows = Array.isArray(json?.competitions) && 
                json.competitions.some((c: any) => Array.isArray(c?.table?.overall) && c.table.overall.length > 0);
              setHasTables(!!anyRows);
            } else {
              setHasTables(false);
            }
          } else {
            setHasTables(false);
          }

          // Process other content with proper type guards
          setHasActivities(events.status === 'fulfilled' && Array.isArray(events.value) && events.value.length > 0);
          setHasPlayers(players.status === 'fulfilled' && Array.isArray(players.value) && players.value.length > 0);
          setHasArticles(articlesResponse.status === 'fulfilled' && typeof articlesResponse.value === 'object' && articlesResponse.value !== null && 'total' in articlesResponse.value && (articlesResponse.value as any).total > 0);
          setHasVideos(youtube.status === 'fulfilled' && youtube.value !== null && typeof youtube.value === 'object' && 'videos' in youtube.value && Array.isArray((youtube.value as any).videos) && (youtube.value as any).videos.length > 0);
          setHasGallery(manifest.status === 'fulfilled' && Array.isArray(manifest.value) && manifest.value.length > 0);
        }
      } catch (error) {
        console.error('Failed to load content data:', error);
        if (!disposed) {
          setHasTables(false);
          setHasActivities(false);
          setHasPlayers(false);
          setHasArticles(false);
          setHasVideos(false);
          setHasGallery(false);
        }
      }
    };

    loadContentData();
    return () => { disposed = true; };
  }, []);

  return {
    categories,
    dynamicNavItems,
    navLoading,
    hasTables,
    hasActivities,
    hasPlayers,
    hasArticles,
    hasVideos,
    hasGallery,
  };
};
